/**
 * The sources table's fixed chrome: the card, the column headers, and the note
 * that explains the two non-obvious statuses. Only the rows differ between the
 * resolved table and its skeleton, so only the rows are a prop.
 *
 * Eager, and deliberately not part of `log-sources-table.tsx`: that component is
 * `lazy()`, and the skeleton renders this chrome for real (P-05 §1) while the
 * chunk is still in flight. Owning the chrome in one place is also what stops the
 * loading and resolved tables from drifting a column apart.
 */
import { cn } from '@/utils/ui/styles'

import { Card } from '@/components/ui/card'
import {
	Table,
	TableBody,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'

import type { ReactNode } from 'react'

/** In render order. `className` carries the first column's extra left padding. */
const COLUMNS: ReadonlyArray<{ label: string; className?: string }> = [
	{ label: 'Source', className: 'px-4' },
	{ label: 'Type' },
	{ label: 'Port' },
	{ label: 'Status' },
	{ label: 'Last log' },
]

const HEAD_CLASS = 'text-muted-foreground text-xs tracking-wide uppercase'

/**
 * What the two non-obvious Log Source statuses mean.
 *
 * Under the table rather than in tooltips because it answers the question the
 * table provokes — "silent since when?" — and a tooltip is a thing you have to
 * already suspect exists.
 */
const STATUS_EXPLANATION =
	'Silent = no logs received in 24 hours (fixed check, feeds health notifications). Awaiting first logs = created but nothing received yet.'

type LogSourceTableFrameProps = {
	/** `<TableRow>`s — real ones, or the skeleton's placeholders. */
	children: ReactNode
}

export const LogSourceTableFrame = ({ children }: LogSourceTableFrameProps) => {
	return (
		<div className="flex flex-col gap-2">
			<Card className="gap-0 overflow-hidden p-0">
				<Table>
					<TableHeader>
						<TableRow>
							{COLUMNS.map((column) => (
								<TableHead
									key={column.label}
									className={cn(HEAD_CLASS, column.className)}
								>
									{column.label}
								</TableHead>
							))}
							{/* Unlabelled: the column holds one button whose own text says what
								it does, and an `ACTIONS` header would be a word describing the
								table rather than the data. */}
							<TableHead className="w-0" />
						</TableRow>
					</TableHeader>

					<TableBody>{children}</TableBody>
				</Table>
			</Card>

			<p className="text-muted-foreground text-xs">{STATUS_EXPLANATION}</p>
		</div>
	)
}
